import React from "react";
import Icon from "@mdi/react";
import { mdiFilePdfBox, mdiFileMusicOutline, mdiVideo, mdiFileDocumentOutline, mdiDownloadOutline } from "@mdi/js";
import styles from "./styles.module.scss";
import type { PublicMessageAttachmentDTO } from "shared/types/MessageContracts";

/**
 * EK-408 polish: render persistente de anexos no histórico do chat.
 * - image/*  → <img>
 * - audio/*  → <audio controls>
 * - video/*  → <video controls>
 * - application/pdf → <iframe> embed
 * - resto    → card clicável com ícone + nome + tamanho
 */
function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface AttachmentPreviewProps {
	attachment: PublicMessageAttachmentDTO;
}

export const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({ attachment }) => {
	const { mimeType, storageUrl, filename, sizeBytes } = attachment;

	if (mimeType.startsWith("image/")) {
		return (
			<a
				href={storageUrl}
				target="_blank"
				rel="noopener noreferrer"
				className={styles.attachmentImageLink}
				title={filename}
			>
				<img
					src={storageUrl}
					alt={filename}
					className={styles.attachmentImage}
					loading="lazy"
				/>
			</a>
		);
	}

	if (mimeType.startsWith("audio/")) {
		return (
			<div className={styles.attachmentAudio}>
				<audio controls preload="metadata" src={storageUrl} className={styles.attachmentAudioPlayer} />
				<a href={storageUrl} download={filename} className={styles.attachmentSubLabel} title={filename}>
					{filename}
				</a>
			</div>
		);
	}

	if (mimeType.startsWith("video/")) {
		return (
			<div className={styles.attachmentVideo}>
				<video controls preload="metadata" src={storageUrl} className={styles.attachmentVideoPlayer} />
				<a href={storageUrl} download={filename} className={styles.attachmentSubLabel} title={filename}>
					{filename}
				</a>
			</div>
		);
	}

	if (mimeType === "application/pdf") {
		return (
			<div className={styles.attachmentPdf}>
				<iframe src={storageUrl} title={filename} className={styles.attachmentPdfFrame} />
				<a href={storageUrl} target="_blank" rel="noopener noreferrer" className={styles.attachmentSubLabel} title={filename}>
					<Icon path={mdiFilePdfBox} size={0.7} /> {filename}
				</a>
			</div>
		);
	}

	// Genérico: card clicável.
	const iconPath = mimeType.startsWith("audio/")
		? mdiFileMusicOutline
		: mimeType.startsWith("video/")
			? mdiVideo
			: mimeType === "application/pdf"
				? mdiFilePdfBox
				: mdiFileDocumentOutline;

	return (
		<a
			href={storageUrl}
			target="_blank"
			rel="noopener noreferrer"
			download={filename}
			className={styles.attachmentGeneric}
			title={filename}
		>
			<Icon path={iconPath} size={1} className={styles.attachmentGenericIcon} />
			<div className={styles.attachmentGenericInfo}>
				<span className={styles.attachmentGenericName}>{filename}</span>
				<span className={styles.attachmentGenericMeta}>
					{mimeType} · {formatBytes(sizeBytes)}
				</span>
			</div>
			<Icon path={mdiDownloadOutline} size={0.8} className={styles.attachmentGenericDownload} />
		</a>
	);
};

interface AttachmentListProps {
	attachments: PublicMessageAttachmentDTO[];
}

export const AttachmentList: React.FC<AttachmentListProps> = ({ attachments }) => {
	if (!attachments?.length) return null;
	return (
		<div className={styles.attachmentList}>
			{attachments.map((att) => (
				<AttachmentPreview key={att.id} attachment={att} />
			))}
		</div>
	);
};
